import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations, LANGUAGES } from './translations';

const LANG_KEY = 'iceswap_lang';
const LanguageContext = createContext(null);

function getInitialLang() {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored && translations[stored]) return stored;
  } catch {
    // localStorage unavailable (private browsing, etc.) — fall through
  }
  if (typeof navigator !== 'undefined') {
    const browserLang = (navigator.language || '').slice(0, 2).toLowerCase();
    if (translations[browserLang]) return browserLang;
  }
  return 'en';
}

/**
 * Site-wide language state, same shape as useTheme: read once from
 * localStorage/browser locale, exposed via context so deeply-nested
 * components (AssetPicker, ListPickerModal rows) can translate without
 * prop-drilling through SwapForm/SplitNowSwap.
 */
export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!translations[code]) return;
    setLangState(code);
    try {
      localStorage.setItem(LANG_KEY, code);
    } catch {
      // ignore — language still applies for this session even if it can't be saved
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = translations[lang] || translations.en;
      let str = dict[key] ?? translations.en[key] ?? key;
      if (vars) {
        Object.keys(vars).forEach((k) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k] ?? '');
        });
      }
      return str;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
